import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Home, ChevronRight, LayoutGrid, List, FolderTree as FolderTreeIcon, X } from 'lucide-react'
import { getProjects, getDocuments, getFolders, getPermissions } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { findMyGrant, accessInWords } from './accessLabels'
import FolderTree from '../../components/documents/FolderTree'
import DocumentCard from '../../components/documents/DocumentCard'
import DocumentGrid from '../../components/documents/DocumentGrid'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import EmptyState from '../../components/ui/EmptyState'
import PageHeader from '../../components/ui/PageHeader'
import ContextualTip from '../../components/ui/ContextualTip'
import HelpPopover from '../../components/help/HelpPopover'
import { FileText, AlertTriangle } from 'lucide-react'

export default function SharedProjectView() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()

  const [docView, setDocView] = useState<'list' | 'grid'>(() => {
    try { return (localStorage.getItem('innovateflow:doc-view:shared') as 'list' | 'grid') || 'list' } catch { return 'list' }
  })

  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => getProjects(),
  })
  const project = projects?.find((p) => p.project_id === projectId)

  const { user } = useAuth()
  const [foldersOpen, setFoldersOpen] = useState(false)
  useEffect(() => {
    if (!foldersOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFoldersOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [foldersOpen])

  // Same query key as the Shared with me list, so this is usually already cached.
  const { data: permissions } = useQuery({
    queryKey: ['shared-project-permissions', projectId],
    queryFn: () => getPermissions(projectId!),
    enabled: !!projectId,
  })
  const myGrant = findMyGrant(permissions, user)

  const { data: folders, isLoading: foldersLoading, isError: foldersError, refetch: refetchFolders } = useQuery({
    queryKey: ['shared-folders', projectId],
    queryFn: () => getFolders(projectId!),
    enabled: !!projectId,
  })

  const { data: documents, isLoading: docsLoading, isError: docsError, refetch: refetchDocs } = useQuery({
    queryKey: ['shared-documents', projectId],
    queryFn: () => getDocuments(projectId!),
    enabled: !!projectId,
  })

  if (foldersLoading || docsLoading) return <LoadingSkeleton rows={4} />

  return (
    <div>
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
        <button onClick={() => navigate('/shared')} className="hover:text-iflow-600 flex items-center gap-1">
          <Home size={14} /> Shared with me
        </button>
        <ChevronRight size={14} />
        <span className="text-gray-800 font-medium">{project?.name || 'Project'}</span>
      </div>

      <PageHeader
        title={project?.name || 'Shared Project'}
        description="A project shared with you. You can open only what your access allows."
        helpKey="shared"
        status={{ label: 'Shared with you', tone: 'info' }}
      />

      <ContextualTip id="shared-project-view-ladder" tone="info">
        {accessInWords(myGrant)} Some documents or detail may be hidden until
        the owner raises your access.{' '}
        <HelpPopover topic="concept-disclosure-ladder" size={14} />
      </ContextualTip>

      <button
        type="button"
        onClick={() => setFoldersOpen(true)}
        className="mt-4 flex items-center gap-1.5 rounded-lg border border-sky-200 bg-white px-3 py-2 text-sm font-medium text-iflow-700 hover:bg-sky-50 lg:hidden"
      >
        <FolderTreeIcon size={14} /> Folders
      </button>

      {foldersOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label="Folders">
          <div className="absolute inset-0 bg-black/30" onClick={() => setFoldersOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85%] overflow-y-auto bg-white p-4 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">Folders</h3>
              <button
                type="button"
                onClick={() => setFoldersOpen(false)}
                aria-label="Close folders"
                className="rounded p-1 text-gray-500 hover:bg-gray-100"
              ><X size={16} /></button>
            </div>
            <FolderTree folders={folders || []} projectId={projectId!} currentFolderId={null} isShared />
          </div>
        </div>
      )}

      <div className="mt-4 flex gap-6">
        <div className="hidden lg:block w-64 flex-shrink-0">
          <div className="light-card p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Folders</h3>
            <FolderTree folders={folders || []} projectId={projectId!} currentFolderId={null} isShared />
          </div>
        </div>

        <div className="min-w-0 flex-1">
          {docsError || foldersError ? (
            <EmptyState
              icon={AlertTriangle}
              title="Couldn't load this project"
              description="Something went wrong fetching this shared project. Your access hasn't changed. Please try again."
              action={
                <button
                  onClick={() => { refetchDocs(); refetchFolders() }}
                  className="px-4 py-2 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800 transition-colors"
                >
                  Retry
                </button>
              }
            />
          ) : !documents?.length ? (
            <EmptyState
              icon={FileText}
              title="No documents to show"
              description="This shared project has no documents at your access level yet. If you expected files here, ask the owner to raise your access."
            />
          ) : (
            <>
              <div className="flex justify-end mb-3">
                <div className="flex items-center border border-sky-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => { setDocView('list'); try { localStorage.setItem('innovateflow:doc-view:shared', 'list') } catch { /* storage unavailable */ } }}
                    className={`p-1.5 ${docView === 'list' ? 'bg-iflow-50 text-iflow-600' : 'text-gray-500 hover:text-gray-600'}`}
                    title="List view" aria-pressed={docView === 'list'}
                  ><List size={14} /></button>
                  <button
                    onClick={() => { setDocView('grid'); try { localStorage.setItem('innovateflow:doc-view:shared', 'grid') } catch { /* storage unavailable */ } }}
                    className={`p-1.5 ${docView === 'grid' ? 'bg-iflow-50 text-iflow-600' : 'text-gray-500 hover:text-gray-600'}`}
                    title="Grid view" aria-pressed={docView === 'grid'}
                  ><LayoutGrid size={14} /></button>
                </div>
              </div>
              {docView === 'grid' ? (
                <DocumentGrid documents={documents} linkPrefix="/shared" />
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <DocumentCard key={doc.document_id} document={doc} linkPrefix="/shared" />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
